import type {
  Workspace,
  WorkspaceApi,
  WorkspaceListResponseApi,
  WorkspaceMember,
  WorkspaceMemberApi,
  WorkspaceOwner,
  WorkspaceOwnerApi,
  WorkspaceScopeCounts,
  WorkspaceScopeCountsApi,
} from "./workspaces.types";

/**
 * API → UI mappers. Currently only exercised by tests; the real API call will
 * pipe its response through `mapWorkspaceListResponse` once backend ships.
 *
 * Keeping these as pure functions so they're trivially testable and reusable
 * (e.g. for an eventual `mockApi.ts` that returns API-shaped data).
 */

export const mapWorkspaceOwner = (
  api: WorkspaceOwnerApi | null,
): WorkspaceOwner | null =>
  api === null ? null : { id: api.id, name: api.name, email: api.email };

export const mapWorkspaceMember = (api: WorkspaceMemberApi): WorkspaceMember => ({
  id: api.id,
  name: api.name,
  avatarUrl: api.avatar_url,
});

export const mapWorkspace = (api: WorkspaceApi): Workspace => ({
  id: api.id,
  tenantId: api.tenant_id,
  name: api.name,
  description: api.description,
  isActive: api.is_active,
  createdAt: api.created_at,
  updatedAt: api.updated_at,
  owner: mapWorkspaceOwner(api.owner),
  members: api.members.map(mapWorkspaceMember),
  membersTotal: api.members_total,
  dataSourceCount: api.data_source_count,
  dataStoryCount: api.data_story_count,
  // Normalise backend role values: "admin" → "owner", null → "viewer".
  currentUserRole:
    api.current_user_role === "admin"
      ? "owner"
      : api.current_user_role ?? "viewer",
});

export const mapWorkspaceScopeCounts = (
  api: WorkspaceScopeCountsApi,
): WorkspaceScopeCounts => ({ my: api.my, all: api.all });

export interface MappedWorkspaceList {
  workspaces: Workspace[];
  scopeCounts: WorkspaceScopeCounts;
  page: number;
  pageSize: number;
  total: number;
}

export const mapWorkspaceListResponse = (
  api: WorkspaceListResponseApi,
): MappedWorkspaceList => ({
  workspaces: api.items.map(mapWorkspace),
  scopeCounts: mapWorkspaceScopeCounts(api.scope_counts),
  page: api.page,
  pageSize: api.page_size,
  total: api.total,
});
